import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../utils/api';
import './HealthProgramApplicationForm.css';

const getQuestionKey = (sectionIdx, questionIdx) => `${sectionIdx}-${questionIdx}`;
const normalizeConditionValue = (value) => String(value ?? '').trim().toLowerCase();
const supportsAnyValueConditionalMatch = (questionType) => ['text', 'textarea'].includes(questionType);
const SUPPORTING_DOCUMENT_KEY_PREFIX = 'supporting-document:';
const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'image/jpeg',
  'image/png'
];
const VALID_FILE_EXTENSIONS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.jpg', '.jpeg', '.png'];

const isSupportingDocumentKey = (questionKey) => typeof questionKey === 'string' && questionKey.startsWith(SUPPORTING_DOCUMENT_KEY_PREFIX);
const buildSupportingDocumentKey = () => `${SUPPORTING_DOCUMENT_KEY_PREFIX}${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const getEmptyAnswerForQuestion = (question) => {
  if (question?.type === 'multi-select') return [];
  if (question?.type === 'checkbox') return false;
  return '';
};

const validateSelectedFile = (file) => {
  if (!file) {
    return 'No file selected.';
  }

  const fileName = file.name.toLowerCase();
  const hasValidExtension = VALID_FILE_EXTENSIONS.some((ext) => fileName.endsWith(ext));
  if (!ALLOWED_FILE_TYPES.includes(file.type) && !hasValidExtension) {
    return 'Invalid file type. Only PDF, DOC, DOCX, XLS, XLSX, JPG, and PNG files are allowed.';
  }

  return '';
};

const HealthProgramApplicationForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useContext(AuthContext);
  const [program, setProgram] = useState(null);
  const [answers, setAnswers] = useState({});
  const [files, setFiles] = useState({});
  const [existingFiles, setExistingFiles] = useState({});
  const [supportingDocuments, setSupportingDocuments] = useState([]);
  const [newSupportingDocuments, setNewSupportingDocuments] = useState([]);
  const [filesMarkedForDeletion, setFilesMarkedForDeletion] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [existingApplication, setExistingApplication] = useState(null);
  const editApplicationId = searchParams.get('editApplication') || '';
  const isEditingExistingApplication = Boolean(existingApplication?._id && existingApplication._id === editApplicationId);

  const loadApplicationFiles = useCallback(async (applicationId) => {
    if (!applicationId) {
      setExistingFiles({});
      setSupportingDocuments([]);
      return;
    }

    const filesResponse = await api.get(`/api/files/application/${applicationId}`);
    const nextExistingFiles = {};
    const nextSupportingDocuments = [];

    (filesResponse.data || []).forEach((fileRecord) => {
      if (isSupportingDocumentKey(fileRecord.questionKey)) {
        nextSupportingDocuments.push(fileRecord);
        return;
      }

      nextExistingFiles[fileRecord.questionKey] = fileRecord;
    });

    setExistingFiles(nextExistingFiles);
    setSupportingDocuments(nextSupportingDocuments);
  }, []);

  const getQuestionMetadata = useCallback(() => {
    const questionById = {};

    if (!program?.sections) {
      return { questionById };
    }

    program.sections.forEach((section, sectionIdx) => {
      (section.questions || []).forEach((question, questionIdx) => {
        if (!question?.questionId) return;

        questionById[question.questionId] = {
          question,
          key: getQuestionKey(sectionIdx, questionIdx)
        };
      });
    });

    return { questionById };
  }, [program]);

  const isQuestionVisible = useCallback((question) => {
    if (!question?.conditional?.enabled) return true;

    const parentQuestionId = question?.conditional?.parentQuestionId;
    if (!parentQuestionId) return true;

    const { questionById } = getQuestionMetadata();
    const parentMeta = questionById[parentQuestionId];

    if (!parentMeta) return true;

    const parentQuestion = parentMeta.question;
    const parentAnswer = answers[parentMeta.key];
    const matchType = question?.conditional?.matchType === 'any' ? 'any' : 'exact';
    const triggerValue = question?.conditional?.showWhenValue;
    const normalizedTrigger = normalizeConditionValue(triggerValue);

    if (matchType === 'any' && supportsAnyValueConditionalMatch(parentQuestion?.type)) {
      return normalizeConditionValue(parentAnswer).length > 0;
    }

    if (parentQuestion?.type === 'checkbox') {
      if (normalizedTrigger === 'true' || normalizedTrigger === 'yes' || normalizedTrigger === '1') {
        return parentAnswer === true;
      }
      if (normalizedTrigger === 'false' || normalizedTrigger === 'no' || normalizedTrigger === '0') {
        return parentAnswer === false;
      }

      return Boolean(parentAnswer);
    }

    if (parentQuestion?.type === 'multi-select') {
      return Array.isArray(parentAnswer) && parentAnswer.some((value) => normalizeConditionValue(value) === normalizedTrigger);
    }

    return normalizeConditionValue(parentAnswer) === normalizedTrigger;
  }, [answers, getQuestionMetadata]);

  const fetchProgram = useCallback(async () => {
    try {
      const response = await api.get(`/api/health-programs/${id}`);
      const loadedProgram = response.data.program;
      setProgram(loadedProgram);
      let userApplication = null;
      
      // Check if user has already applied
      try {
        const applicationsResponse = await api.get(`/api/health-program-applications?programId=${id}&userOnly=true`);
        if (applicationsResponse.data.applications && applicationsResponse.data.applications.length > 0) {
          userApplication = applicationsResponse.data.applications[0];
          setExistingApplication(userApplication);

          if (editApplicationId && userApplication._id === editApplicationId) {
            await loadApplicationFiles(userApplication._id);
          } else {
            setExistingFiles({});
            setSupportingDocuments([]);
          }
        } else {
          setExistingApplication(null);
          setExistingFiles({});
          setSupportingDocuments([]);
        }
      } catch (appErr) {
        console.error('Error checking existing applications:', appErr);
      }
      
      // Initialize answers
      const initAnswers = {};
      if (loadedProgram.sections) {
        loadedProgram.sections.forEach((section, sIdx) => {
          section.questions.forEach((q, qIdx) => {
            const key = getQuestionKey(sIdx, qIdx);
            initAnswers[key] = getEmptyAnswerForQuestion(q);

            if (userApplication?.answers && editApplicationId && userApplication._id === editApplicationId) {
              if (Object.prototype.hasOwnProperty.call(userApplication.answers, key)) {
                initAnswers[key] = userApplication.answers[key];
              }
            }
          });
        });
      }
      setAnswers(initAnswers);
      setFiles({});
      setNewSupportingDocuments([]);
      setFilesMarkedForDeletion([]);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching program:', err);
      setError(err.response?.data?.message || 'Error fetching program details');
      setLoading(false);
    }
  }, [editApplicationId, id, loadApplicationFiles]);

  useEffect(() => {
    fetchProgram();
  }, [fetchProgram]);

  const handleChange = (key, value) => {
    setAnswers({ ...answers, [key]: value });
    // Clear validation error for this field
    if (validationErrors[key]) {
      const newErrors = { ...validationErrors };
      delete newErrors[key];
      setValidationErrors(newErrors);
    }
  };

  const handleFileChange = (questionKey, file) => {
    if (file) {
      const validationMessage = validateSelectedFile(file);
      if (validationMessage) {
        alert(validationMessage);
        return;
      }
      
      setFiles((prev) => ({ ...prev, [questionKey]: file }));
      handleChange(questionKey, file.name);
    } else {
      setFiles((prev) => {
        const updatedFiles = { ...prev };
        delete updatedFiles[questionKey];
        return updatedFiles;
      });

      if (!existingFiles[questionKey]) {
        handleChange(questionKey, '');
      }
    }
  };

  const clearPendingQuestionFile = (questionKey) => {
    setFiles((prev) => {
      const updatedFiles = { ...prev };
      delete updatedFiles[questionKey];
      return updatedFiles;
    });

    if (!existingFiles[questionKey]) {
      handleChange(questionKey, '');
    }
  };

  const markFileForDeletion = (fileId) => {
    setFilesMarkedForDeletion((prev) => (prev.includes(fileId) ? prev : [...prev, fileId]));
  };

  const removeExistingQuestionFile = (questionKey) => {
    const fileRecord = existingFiles[questionKey];
    if (!fileRecord) return;

    markFileForDeletion(fileRecord._id);
    setExistingFiles((prev) => {
      const nextFiles = { ...prev };
      delete nextFiles[questionKey];
      return nextFiles;
    });
    clearPendingQuestionFile(questionKey);
    handleChange(questionKey, '');
  };

  const handleSupportingDocumentsChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    const validFiles = [];
    const invalidMessages = [];

    selectedFiles.forEach((file) => {
      const validationMessage = validateSelectedFile(file);
      if (validationMessage) {
        invalidMessages.push(`${file.name}: ${validationMessage}`);
      } else {
        validFiles.push(file);
      }
    });

    if (validFiles.length > 0) {
      setNewSupportingDocuments((prev) => [...prev, ...validFiles]);
    }

    if (invalidMessages.length > 0) {
      alert(invalidMessages.join('\n'));
    }

    event.target.value = '';
  };

  const removePendingSupportingDocument = (indexToRemove) => {
    setNewSupportingDocuments((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const removeExistingSupportingDocument = (fileId) => {
    markFileForDeletion(fileId);
    setSupportingDocuments((prev) => prev.filter((fileRecord) => fileRecord._id !== fileId));
  };

  const uploadApplicationFile = async (applicationId, questionKey, file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('applicationId', applicationId);
    formData.append('questionKey', questionKey);

    await api.post('/api/files/upload-application', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  };

  const syncApplicationFiles = async (applicationId) => {
    const uploadErrors = [];
    const deletedIds = new Set();

    for (const fileId of filesMarkedForDeletion) {
      try {
        await api.delete(`/api/files/${fileId}`);
        deletedIds.add(fileId);
      } catch (deleteErr) {
        uploadErrors.push(`Could not delete an existing file: ${deleteErr.response?.data?.message || deleteErr.message}`);
      }
    }

    for (const [questionKey, file] of Object.entries(files)) {
      const currentFile = existingFiles[questionKey];

      if (currentFile && !deletedIds.has(currentFile._id)) {
        try {
          await api.delete(`/api/files/${currentFile._id}`);
          deletedIds.add(currentFile._id);
        } catch (deleteErr) {
          uploadErrors.push(`${currentFile.originalname}: ${deleteErr.response?.data?.message || deleteErr.message}`);
          continue;
        }
      }

      try {
        await uploadApplicationFile(applicationId, questionKey, file);
      } catch (uploadErr) {
        uploadErrors.push(`${file.name}: ${uploadErr.response?.data?.message || uploadErr.message || 'Upload failed'}`);
      }
    }

    for (const file of newSupportingDocuments) {
      try {
        await uploadApplicationFile(applicationId, buildSupportingDocumentKey(), file);
      } catch (uploadErr) {
        uploadErrors.push(`${file.name}: ${uploadErr.response?.data?.message || uploadErr.message || 'Upload failed'}`);
      }
    }

    return uploadErrors;
  };

  const handleMultiSelectChange = (key, option, checked) => {
    let current = Array.isArray(answers[key]) ? answers[key] : [];
    if (checked) {
      if (!current.includes(option)) current.push(option);
    } else {
      current = current.filter(item => item !== option);
    }
    handleChange(key, current);
  };

  const validateForm = () => {
    const errors = {};
    
    if (!program || !program.sections) return errors;

    program.sections.forEach((section, sIdx) => {
      section.questions.forEach((question, qIdx) => {
        if (!isQuestionVisible(question)) {
          return;
        }

        const key = getQuestionKey(sIdx, qIdx);
        const answer = answers[key];

        if (question.required) {
          if (question.type === 'multi-select') {
            if (!Array.isArray(answer) || answer.length === 0) {
              errors[key] = 'This field is required';
            }
          } else if (!answer || (typeof answer === 'string' && answer.trim() === '')) {
            errors[key] = 'This field is required';
          }
        }
      });
    });

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      alert('Please fill in all required fields');
      window.scrollTo(0, 0);
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const portal = program.category || 'health';
      const normalizedProgramType = Array.isArray(program.programType)
        ? program.programType.filter(Boolean)
        : (program.programType ? [program.programType] : []);

      if (normalizedProgramType.length === 0) {
        setError('This program is missing a program type. Please contact support before applying.');
        setSubmitting(false);
        return;
      }

      const applicationData = {
        applicantName: `${currentUser.firstName} ${currentUser.lastName}`,
        healthProgramId: program._id,
        programTitle: program.title,
        programType: normalizedProgramType,
        portal,
        answers
      };

      if (isEditingExistingApplication) {
        await api.put(`/api/health-program-applications/${existingApplication._id}`, applicationData);
        const uploadErrors = await syncApplicationFiles(existingApplication._id);

        if (uploadErrors.length > 0) {
          const failureMessage = `Your application was updated, but some file changes could not be completed:\n\n${uploadErrors.join('\n')}\n\nPlease review your submission and try again.`;
          setError(failureMessage);
          setSubmitting(false);
          window.scrollTo(0, 0);
          alert(failureMessage);
          return;
        }

        alert('Application updated successfully!');
        navigate(`/review-health-application/${existingApplication._id}?view=applicant`);
        return;
      }

      const response = await api.post('/api/health-program-applications', applicationData);
      const applicationId = response.data.application?._id;
      const uploadErrors = await syncApplicationFiles(applicationId);

      if (uploadErrors.length > 0) {
        if (applicationId) {
          try {
            await api.delete(`/api/health-program-applications/${applicationId}`);
          } catch (rollbackErr) {
            console.error('Error rolling back application after failed file upload:', rollbackErr);
          }
        }

        const failureMessage = `Your application was not submitted because one or more files could not be uploaded:\n\n${uploadErrors.join('\n')}\n\nPlease try again.`;
        setError(failureMessage);
        setSubmitting(false);
        window.scrollTo(0, 0);
        alert(failureMessage);
        return;
      }

      alert('Application submitted successfully!');
      navigate('/dashboard');
    } catch (err) {
      console.error('Submission error:', err);
      const errorMessage = err.response?.data?.message || 'Error submitting application';
      setError(errorMessage);
      
      // If duplicate application error, refresh to show the existing application notice
      if (err.response?.data?.existingApplicationId) {
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
      
      setSubmitting(false);
      window.scrollTo(0, 0);
    }
  };

  const renderQuestion = (question, sectionIdx, questionIdx) => {
    if (!isQuestionVisible(question)) {
      return null;
    }

    const key = getQuestionKey(sectionIdx, questionIdx);
    const answer = answers[key];
    const hasError = validationErrors[key];
    const existingFile = existingFiles[key];
    const pendingFile = files[key];
    const authToken = localStorage.getItem('authToken');

    return (
      <div key={key} className={`question-block ${hasError ? 'has-error' : ''}`}>
        <label className="question-label">
          {question.label}
          {question.required && <span className="required">*</span>}
        </label>
        
        {question.helpText && (
          <p className="help-text">{question.helpText}</p>
        )}

        {question.type === 'text' && (
          <input
            type="text"
            value={answer || ''}
            onChange={(e) => handleChange(key, e.target.value)}
            placeholder={question.placeholder}
            className="form-input"
          />
        )}

        {question.type === 'textarea' && (
          <textarea
            value={answer || ''}
            onChange={(e) => handleChange(key, e.target.value)}
            placeholder={question.placeholder}
            rows={question.rows || 4}
            className="form-textarea"
          />
        )}

        {question.type === 'number' && (
          <input
            type="number"
            value={answer || ''}
            onChange={(e) => handleChange(key, e.target.value)}
            placeholder={question.placeholder}
            className="form-input"
          />
        )}

        {question.type === 'date' && (
          <input
            type="date"
            value={answer || ''}
            onChange={(e) => handleChange(key, e.target.value)}
            className="form-input"
          />
        )}

        {question.type === 'dropdown' && (
          <select
            value={answer || ''}
            onChange={(e) => handleChange(key, e.target.value)}
            className="form-select"
          >
            <option value="">-- Select an option --</option>
            {question.options && question.options.map((option, idx) => (
              <option key={idx} value={option}>{option}</option>
            ))}
          </select>
        )}

        {question.type === 'checkbox' && (
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={answer || false}
              onChange={(e) => handleChange(key, e.target.checked)}
            />
            <span>{question.checkboxLabel || 'Yes'}</span>
          </label>
        )}

        {question.type === 'multi-select' && (
          <div className="checkbox-group">
            {question.options && question.options.map((option, idx) => (
              <label key={idx} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={Array.isArray(answer) && answer.includes(option)}
                  onChange={(e) => handleMultiSelectChange(key, option, e.target.checked)}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        )}

        {question.type === 'file' && (
          <div className="file-upload-group">
            {existingFile && (
              <div className="uploaded-file-row">
                <a
                  href={`${api.defaults.baseURL}/api/files/${existingFile._id}?token=${encodeURIComponent(authToken)}`}
                  className="uploaded-file-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Current file: {existingFile.originalname}
                </a>
                <button type="button" className="file-action-btn file-action-delete" onClick={() => removeExistingQuestionFile(key)}>
                  Remove
                </button>
              </div>
            )}

            {pendingFile && (
              <div className="uploaded-file-row pending-file-row">
                <span>Selected file: {pendingFile.name}</span>
                <button type="button" className="file-action-btn" onClick={() => clearPendingQuestionFile(key)}>
                  Clear
                </button>
              </div>
            )}

            <input
              type="file"
              onChange={(e) => handleFileChange(key, e.target.files[0])}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
              className="form-file"
            />

            {(existingFile || pendingFile) && (
              <small className="help-text">Uploading a new file replaces the current document for this question.</small>
            )}
          </div>
        )}

        {hasError && <span className="error-text">{validationErrors[key]}</span>}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="health-application-container">
        <div className="loading">Loading program details...</div>
      </div>
    );
  }

  if (error && !program) {
    return (
      <div className="health-application-container">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  if (!program) {
    return (
      <div className="health-application-container">
        <div className="error-message">Program not found</div>
      </div>
    );
  }

  return (
    <div className="health-application-container">
      <div className="application-header">
        <h1>{isEditingExistingApplication ? `Edit Application for ${program.title}` : `Apply for ${program.title}`}</h1>
        <div className="program-info">
          <span className="program-type">{program.programType}</span>
          <span className="program-target">{Array.isArray(program.targetPopulation) ? program.targetPopulation.join(', ') : program.targetPopulation}</span>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {existingApplication && !isEditingExistingApplication && (
        <div className="existing-application-notice">
          <h3>✓ Application Already Submitted</h3>
          <p>You have already submitted an application for this program.</p>
          <div className="application-status">
            <strong>Status:</strong> <span className={`status-badge status-${existingApplication.status}`}>
              {existingApplication.status.charAt(0).toUpperCase() + existingApplication.status.slice(1)}
            </span>
          </div>
          <div className="application-actions">
            <button 
              onClick={() => navigate(`/review-health-application/${existingApplication._id}?view=applicant`)}
              className="btn-view-application"
            >
              View Your Application
            </button>
            <button
              onClick={() => navigate(`/apply-health-program/${program._id}?editApplication=${existingApplication._id}`)}
              className="btn-view-application btn-edit-application"
            >
              Edit Your Application
            </button>
            <button 
              onClick={() => navigate(`/health-programs${program?.category ? `?category=${program.category}` : ''}`)}
              className="btn-back"
            >
              Back to Programs
            </button>
          </div>
        </div>
      )}

      {(!existingApplication || isEditingExistingApplication) && (
        <>
          {program.description && (
            <div className="program-description">
              <h3>About This Program</h3>
              <div dangerouslySetInnerHTML={{ __html: program.description }} />
            </div>
          )}

          {program.eligibilityCriteria && (
            <div className="eligibility-section">
              <h3>Eligibility Requirements</h3>
              <div dangerouslySetInnerHTML={{ __html: program.eligibilityCriteria }} />
            </div>
          )}

          <form onSubmit={handleSubmit} className="application-form">
            {program.sections && program.sections.length > 0 ? (
              program.sections.map((section, sIdx) => (
                <div key={sIdx} className="form-section">
                  <h2 className="section-title">{section.title || section.label}</h2>
                  {section.description && (
                    <p className="section-description">{section.description}</p>
                  )}
                  <div className="questions-container">
                    {section.questions.map((question, qIdx) =>
                      renderQuestion(question, sIdx, qIdx)
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="no-sections">
                <p>This program does not have an application form configured yet. Please contact the program administrator.</p>
              </div>
            )}

            {program.sections && program.sections.length > 0 && (
              <div className="form-section additional-documents-section">
                <h2 className="section-title">Submission Documents</h2>
                <p className="section-description">
                  Add or remove supporting documents attached to this submission. These appear on the application review page.
                </p>

                {supportingDocuments.length > 0 && (
                  <div className="supporting-documents-list">
                    {supportingDocuments.map((fileRecord) => {
                      const authToken = localStorage.getItem('authToken');
                      return (
                        <div key={fileRecord._id} className="uploaded-file-row">
                          <a
                            href={`${api.defaults.baseURL}/api/files/${fileRecord._id}?token=${encodeURIComponent(authToken)}`}
                            className="uploaded-file-link"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {fileRecord.originalname}
                          </a>
                          <button type="button" className="file-action-btn file-action-delete" onClick={() => removeExistingSupportingDocument(fileRecord._id)}>
                            Remove
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {newSupportingDocuments.length > 0 && (
                  <div className="supporting-documents-list pending-documents-list">
                    {newSupportingDocuments.map((file, index) => (
                      <div key={`${file.name}-${index}`} className="uploaded-file-row pending-file-row">
                        <span>{file.name}</span>
                        <button type="button" className="file-action-btn" onClick={() => removePendingSupportingDocument(index)}>
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <input
                  type="file"
                  multiple
                  onChange={handleSupportingDocumentsChange}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                  className="form-file"
                />
              </div>
            )}

            {program.sections && program.sections.length > 0 && (
              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => navigate(`/health-programs${program?.category ? `?category=${program.category}` : ''}`)}
                  className="btn-cancel"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-submit"
                >
                  {submitting ? (isEditingExistingApplication ? 'Saving...' : 'Submitting...') : (isEditingExistingApplication ? 'Save Changes' : 'Submit Application')}
                </button>
              </div>
            )}
          </form>
        </>
      )}
    </div>
  );
};

export default HealthProgramApplicationForm;
